import H from '@here/maps-api-for-javascript';
import { isEqual } from 'lodash';
import { createTourUnselectedSvg, createOrderUnassignedSvg } from '../../../../icons/marker';
import { isUnassignedJob } from '../../../../utils/map/MapFeatureFilters';
import { colors } from '../../../../global/variables';

const { defaultStopMarkerColor } = colors;
const DEFAULT_MARKER_COLOR = { inner: defaultStopMarkerColor, outer: defaultStopMarkerColor };

const UNSELECTED_ANCHOR = { x: 5, y: 5 };
const UNASSIGNED_ANCHOR = { x: 5, y: 6 };

export function getUnselectedStopMarkerIcon(feature) {
  if (isUnassignedJob(feature)) {
    return createOrderUnassignedSvg(10, 11, false);
  }

  const jobMarkerColor = feature.properties?.jobMarkerColor || {
    delivery: DEFAULT_MARKER_COLOR,
    pickup: DEFAULT_MARKER_COLOR,
  };

  const types = feature.properties?.types || ['delivery'];
  const typesObj = types.reduce((t, item) => {
    t[item] = (t[item] || 0) + 1;
    return t;
  }, {});

  const oneType = Object.keys(typesObj).length === 1;
  const colorToUse = !oneType
    ? { inner: 'grey', outer: 'grey' }
    : Object.keys(typesObj)[0] === 'pickup'
    ? jobMarkerColor.pickup
    : jobMarkerColor.delivery;

  return createTourUnselectedSvg(colorToUse.inner, colorToUse.outer, false, false);
}

export function setUnselectedStopMarkerIcon(marker, feature) {
  const unassigned = isUnassignedJob(feature);
  const anchor = unassigned ? UNASSIGNED_ANCHOR : UNSELECTED_ANCHOR;
  const svg = getUnselectedStopMarkerIcon(feature);
  marker.setIcon(new H.map.Icon(svg, { anchor }));
}

export function createUnselectedStopMarker(coords, feature) {
  const marker = new H.map.Marker({ lat: coords[1], lng: coords[0] }, { volatility: true });
  marker.setData(feature.properties);
  setUnselectedStopMarkerIcon(marker, feature);
  return marker;
}

export function updateUnselectedStopGroupMarkers(
  geoJson,
  group,
  translationsRef,
  map,
  attachHoverEventsFn,
  dispatch,
) {
  const features = geoJson?.geo?.features || [];
  const newMarkerIds = new Set();
  const existingMarkers = new Map();

  group.getObjects().forEach((marker) => {
    existingMarkers.set(marker.id, marker);
  });

  features.forEach((feature, idx) => {
    if (feature.geometry.type === 'Point') {
      const coords = feature.geometry.coordinates;
      const props = feature.properties || {};
      const id = props.jobId || props.id || `${coords[0]}_${coords[1]}_${props.routeId}_${props.activityType ?? idx}`;
      newMarkerIds.add(id);

      const existingMarker = existingMarkers.get(id);

      if (existingMarker) {
        const currentData = existingMarker.getData() || {};
        const needsUpdate =
          currentData.routeId !== props.routeId ||
          currentData.isUnassigned !== props.isUnassigned ||
          !isEqual(currentData.jobMarkerColor, props.jobMarkerColor) ||
          !isEqual(currentData.types, props.types);

        if (needsUpdate) {
          existingMarker.setData(props);
          setUnselectedStopMarkerIcon(existingMarker, feature);
        }
      } else {
        const marker = createUnselectedStopMarker(coords, feature);
        marker.id = id;
        attachHoverEventsFn(marker, translationsRef, map, dispatch);
        group.addObject(marker);
      }
    }
  });

  [...group.getObjects()].forEach((marker) => {
    if (!newMarkerIds.has(marker.id)) {
      group.removeObject(marker);
    }
  });

  group.setVisibility(true);
}
